import { Trans } from '@lingui/macro'
import { Cancel, CheckCircle, CheckCircleOutlineOutlined, Circle, CircleOutlined, HighlightOffOutlined } from '@mui/icons-material'
import { Box, Grid, Step, StepIcon, StepIconProps, StepLabel, Stepper, SvgIcon, Typography } from '@mui/material'
import React, { FC, useEffect } from 'react'
import useMeasure from 'react-use-measure'
import FormStepperDropdown from './components/form-stepper/form-stepper-dropdown'
import { getValidCurrentStepIndex, sectionConditionId } from './FormHelpersConditions'
import { StepperFeatStickyComponentController } from './StepperFeatStickyComponentController'

interface FormStepperProps {
  formType: 'editable' | 'pdf' | 'fillable-pdf' | 'printable'
  disabledIds: string[]
  sections: any[]
  parsedErrors: any[]
  currentStep: number
  handleStepChange: (newIndex: number) => void
  renderStepLabel: (index: number, section: any) => JSX.Element
  disabled: boolean
  ButtonsElement: React.ReactNode
  downloadButton: boolean
  saveButton: boolean
  renderDropdown?: boolean
  setSmallSpace: (smallSpace: { stepper: boolean, buttons: boolean }) => void
  smallSpace: boolean
  extraButtons: Array<() => void>
}

const FormStepper = (
  { disabledIds, downloadButton, saveButton, extraButtons = [], formType, currentStep, sections, handleStepChange, disabled, parsedErrors, renderStepLabel, ButtonsElement, setSmallSpace, smallSpace, ...props }: FormStepperProps): JSX.Element => {
  const [ref, bounds] = useMeasure()
  let guttersWidth = 0
  if (downloadButton) {
    guttersWidth += 170
  }
  if (saveButton) {
    guttersWidth += 95
    if (downloadButton) {
      guttersWidth += 16
    }
  }
  if (extraButtons.length > 0) {
    guttersWidth += extraButtons.length * 290
  }

  const renderDropdown: boolean = Boolean(smallSpace.stepper || props.renderDropdown)
  const sectionsToRedner = sections.filter((section, index) => !disabledIds.includes(sectionConditionId + String(index)))

  const stepperSpace = bounds.width - guttersWidth - sectionsToRedner.length * 140
  const buttonsSpace = renderDropdown ? bounds.width - guttersWidth - 300 : bounds.width - guttersWidth - sectionsToRedner.length * 140

  let buttonsContainerWidth = guttersWidth
  if (buttonsSpace < 0) {
    buttonsContainerWidth = 0
    if (downloadButton) {
      buttonsContainerWidth += 66
    }
    if (saveButton) {
      buttonsContainerWidth += 66
    }
    if (extraButtons.length > 0) {
      buttonsContainerWidth += extraButtons.length * 66
    }
  }

  useEffect(() => {
    setSmallSpace({
      stepper: stepperSpace < 0,
      buttons: buttonsSpace < 0
    })
  }, [stepperSpace, buttonsSpace])

  return (
    <Grid ref={ref} container direction='row' wrap='nowrap' sx={{ mb: '7px', mt: '16px' }} gap='16px'>
      {/* {stepperSpace - guttersWidth > 0 && !renderDropdown &&
        <Grid sx={{ maxWidth: `${guttersWidth}px`, minWidth: `${guttersWidth}px` }} />} */}
      <Grid item xs>
        {renderDropdown &&
          <Box sx={{ gap: '10px', mt: '16px' }}>
            <Typography variant='body2' sx={{ fontSize: '14px', lineHeight: '19px', fontWeight: 400 }}>
              <Trans>FORM_STEPPER_CURRENT_SECTION</Trans>
            </Typography>
            <FormStepperDropdown
              parsedErrors={parsedErrors}
              renderIcons={formType === 'editable'}
              currentStep={currentStep}
              handleStepChange={handleStepChange}
              sections={sections.map((section, index) => ({ ...section, trueIndex: index })).filter((section, index) => !disabledIds.includes(sectionConditionId + String(index)))}
              renderStepLabel={renderStepLabel}
            />
          </Box>}
        {!renderDropdown &&
          <Stepper
            nonLinear
            activeStep={getValidCurrentStepIndex({
              realIndex: currentStep,
              disabledIds
            })}
            orientation='horizontal'
            alternativeLabel
          >
            {sections.map((section, index) => {
              if (disabledIds.includes(sectionConditionId + String(index))) {
                return null
              }

              const hasErrors = parsedErrors.filter(
                (obj) => index === obj.sectionIndex
              ).length > 0
              return (
                <Step
                  itemType='step'
                  id='step'
                  key={index}
                  style={{ cursor: 'pointer' }}
                  onClick={(e) => {
                    handleStepChange(index)
                  }}
                  completed={index < currentStep}
                >
                  <StepLabel error={hasErrors && formType === 'editable'} id='label' StepIconComponent={formType === 'editable' ? EditableFormIcon : ReadOnlyFormIcon}>
                    <Typography sx={{ fontSize: '14px', lineHeight: '19px', fontWeight: 600 }}>
                      {renderStepLabel(index, section)}
                    </Typography>
                  </StepLabel>
                </Step>
              )
            })}
          </Stepper>}
      </Grid>
      <Grid sx={{ maxWidth: `${buttonsContainerWidth}px`, minWidth: `${buttonsContainerWidth}px` }}>
        {ButtonsElement}
      </Grid>
    </Grid>

  )
}

type FormStepperFeatStickyComponentControllerProps = {
  stickyComponentRef?: React.RefObject<HTMLDivElement>
  isStickyCallback?: (isSticky: boolean) => void
  children: React.ReactNode
} & FormStepperProps

/**
 * This component decides how to render base FormStepper depending on whether the stickyComponentRef element is sticky or not.
 * @param {RefObject<HTMLElement>} props.stickyComponentRef - The ref for the sticky component.
 * @param {Function} props.isStickyCallback - Callback function to notify when the sticky state changes.
 * @param {ReactElement} props - base FormStepper props
 * @returns {ReactElement} The rendered component.
 */
const FormStepperFeatStickyComponentController: FC<FormStepperFeatStickyComponentControllerProps> =
  ({ stickyComponentRef, isStickyCallback, children, ...props }) => {
    return (
      <StepperFeatStickyComponentController
        stickyComponentRef={stickyComponentRef}
        isStickyCallback={isStickyCallback}
      >
        <FormStepper {...props} />
      </StepperFeatStickyComponentController>
    )
  }

export const ReadOnlyFormIcon = ({ ...props }: StepIconProps): JSX.Element => {
  const { completed = false, active = false, error = false } = props

  if (active) {
    return <StepIcon icon={<SvgIcon component={Circle} color='primary' sx={{ width: '24px', height: '24px' }} />} />
  }

  return <StepIcon icon={<SvgIcon component={CircleOutlined} color='primary' sx={{ width: '24px', height: '24px' }} />} />
}

export const EditableFormIcon = ({ ...props }: StepIconProps): JSX.Element => {
  const { completed = false, active = false, error = false } = props

  if (active) {
    if (error) {
      return <StepIcon icon={<SvgIcon component={Cancel} sx={{ width: '24px', height: '24px', color: 'error.main' }} />} />
    } else {
      return <StepIcon icon={<SvgIcon component={CheckCircle} sx={{ width: '24px', height: '24px', color: 'success.main' }} />} />
    }
  }

  if (!error && completed) {
    return <StepIcon icon={<SvgIcon component={CheckCircleOutlineOutlined} sx={{ width: '24px', height: '24px', color: 'success.main' }} />} />
  }

  if (error) {
    return <StepIcon icon={<SvgIcon component={HighlightOffOutlined} sx={{ width: '24px', height: '24px', color: 'error.main' }} />} />
  }

  return <StepIcon icon={<SvgIcon component={CircleOutlined} color='primary' sx={{ width: '24px', height: '24px' }} />} />
}

export { FormStepperFeatStickyComponentController as FormStepper }

